import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider as ThemeProviderV5 } from '@mui/material/styles';
import { useAuth } from '../auth';
import { useColorMode } from '../colorMode/colorMode.context';
import {
    buildTheme,
    buildThemeV5,
    DEFAULT_BRAND_COLORS,
    DEFAULT_GLOBAL_COLORS,
    DARK_SURFACE,
} from '../../utils/buildTheme';
import { getBrandingSessionKey } from '../../utils/brandingSessionKey';
import {
    isFontFileUrl,
    isHttpUrl,
    fontFormatHint,
    isGoogleFontsCssUrl,
    resolveBrandFontFamily,
} from '../../utils/brandingAssets';

const EMPTY_BRANDING = {
    logo_url: null,
    colors: { primary: null, secondary: null, text: null, background: null },
    fonts: { primary: null, secondary: null },
    custom_stylesheet: null,
};

const cacheKeyFor = (dq_insider_id) => (
    dq_insider_id ? `dq_workspace_branding_${dq_insider_id}` : null
);

const readCache = (dq_insider_id) => {
    const key = cacheKeyFor(dq_insider_id);
    if (!key) return EMPTY_BRANDING;
    try {
        const c = localStorage.getItem(key);
        return c ? JSON.parse(c) : EMPTY_BRANDING;
    } catch {
        return EMPTY_BRANDING;
    }
};

export const clearBrandingCacheForWorkspace = (dq_insider_id) => {
    const key = cacheKeyFor(dq_insider_id);
    if (key) {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
    try { localStorage.removeItem('dq_workspace_branding'); } catch { /* ignore */ }
};

export const clearAllBrandingCaches = () => {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const k = localStorage.key(i);
            if (k && k.startsWith('dq_workspace_branding')) keys.push(k);
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore */ }
};

export { getBrandingSessionKey } from '../../utils/brandingSessionKey';

const EMPTY_ACCESS = {
    mode: 'off',
    applied: false,
    user_can_view: false,
    user_can_edit: false,
};

const BrandingContext = createContext({
    branding: EMPTY_BRANDING,
    brandingAccess: EMPTY_ACCESS,
    userCanViewAppearance: false,
    userCanEditBranding: false,
    refreshBranding: () => {},
    setBranding: () => {},
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
    const { authToken, authUser } = useAuth();
    const { mode } = useColorMode();
    const sessionKey = getBrandingSessionKey(authUser);
    const wsId = authUser && authUser.dq_insider_id;
    const [branding, setBrandingState] = useState(EMPTY_BRANDING);
    const [brandingAccess, setBrandingAccess] = useState(EMPTY_ACCESS);
    const [stylesheetCss, setStylesheetCss] = useState('');

    const applyCssDefaults = useCallback(() => {
        const root = document.documentElement;
        const isDark = mode === 'dark';
        const D = DEFAULT_BRAND_COLORS;
        const G = DEFAULT_GLOBAL_COLORS;
        root.style.setProperty('--brand-primary', D.primary);
        root.style.setProperty('--brand-secondary', D.secondary);
        root.style.setProperty('--brand-text', isDark ? DARK_SURFACE.text : D.text);
        root.style.setProperty('--brand-bg', isDark ? DARK_SURFACE.background : D.background);
        root.style.setProperty('--brand-surface', isDark ? DARK_SURFACE.paper : '#ffffff');
        root.style.setProperty('--brand-border', isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB');
        root.style.setProperty('--brand-text-muted', isDark ? DARK_SURFACE.textSecondary : '#999');
        root.style.setProperty('--brand-section-bg', D.background);
        root.style.setProperty('--brand-primary-title', D.text);
        root.style.setProperty('--brand-secondary-title', D.secondary);
        root.style.setProperty('--brand-global-1', G.global_1);
        root.style.setProperty('--brand-global-2', G.global_2);
        root.style.setProperty('--brand-global-3', G.global_3);
        root.style.setProperty('--brand-font-primary', '');
        root.style.setProperty('--brand-font-secondary', '');
    }, [mode]);

    const setBranding = useCallback((b, forWorkspaceId) => {
        const next = b || EMPTY_BRANDING;
        setBrandingState(next);
        const key = cacheKeyFor(forWorkspaceId || wsId);
        if (key) {
            try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore quota */ }
        }
    }, [wsId]);

    const applyBrandingFromAuth = useCallback((user) => {
        if (!user) return;
        const access = user.branding_access || EMPTY_ACCESS;
        setBrandingAccess(access);
        if (access.applied === false) {
            setBrandingState(EMPTY_BRANDING);
            applyCssDefaults();
            return;
        }
        if (user.branding) {
            setBranding(user.branding, user.dq_insider_id);
        } else {
            setBrandingState(readCache(user.dq_insider_id));
        }
    }, [setBranding, applyCssDefaults]);

    const refreshBranding = useCallback(() => {
        if (!authToken || !wsId) return;
        applyBrandingFromAuth(authUser);
    }, [authToken, wsId, authUser, applyBrandingFromAuth]);

    useEffect(() => {
        if (!authToken) {
            setBrandingState(EMPTY_BRANDING);
            setBrandingAccess(EMPTY_ACCESS);
            applyCssDefaults();
            return;
        }
        if (!sessionKey || !wsId) return;

        setBrandingState(EMPTY_BRANDING);
        setBrandingAccess(EMPTY_ACCESS);
        applyCssDefaults();
        applyBrandingFromAuth(authUser);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, sessionKey]);

    useEffect(() => {
        if (!authToken || !wsId) return;
        applyBrandingFromAuth(authUser);
    }, [authToken, wsId, authUser?.branding, authUser?.branding_access, applyBrandingFromAuth, authUser]);

    useEffect(() => {
        const root = document.documentElement;
        const c = (branding && branding.colors) || {};
        const f = (branding && branding.fonts) || {};
        const D = DEFAULT_BRAND_COLORS;
        const G = DEFAULT_GLOBAL_COLORS;
        const isDark = mode === 'dark';

        root.style.setProperty('--brand-primary', c.primary || D.primary);
        root.style.setProperty('--brand-secondary', c.secondary || D.secondary);
        root.style.setProperty('--brand-text', isDark ? DARK_SURFACE.text : (c.text || D.text));
        root.style.setProperty('--brand-bg', isDark ? DARK_SURFACE.background : (c.background || D.background));
        root.style.setProperty('--brand-surface', isDark ? DARK_SURFACE.paper : '#ffffff');
        root.style.setProperty('--brand-border', isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB');
        root.style.setProperty('--brand-text-muted', isDark ? DARK_SURFACE.textSecondary : '#999');
        root.style.setProperty('--brand-section-bg', c.section_bg || c.background || D.background);
        root.style.setProperty('--brand-primary-title', c.primary_title || c.text || D.text);
        root.style.setProperty('--brand-secondary-title', c.secondary_title || c.secondary || D.secondary);
        root.style.setProperty('--brand-global-1', c.global_1 || G.global_1);
        root.style.setProperty('--brand-global-2', c.global_2 || G.global_2);
        root.style.setProperty('--brand-global-3', c.global_3 || G.global_3);
        root.style.setProperty('--brand-font-primary', resolveBrandFontFamily(f.primary, 'BrandPrimaryFont') || '');
        root.style.setProperty('--brand-font-secondary', resolveBrandFontFamily(f.secondary, 'BrandSecondaryFont') || '');
    }, [branding, mode]);

    useEffect(() => {
        const sheetUrl = (branding && branding.custom_stylesheet) || '';
        if (!sheetUrl || !isHttpUrl(sheetUrl)) {
            setStylesheetCss('');
            return undefined;
        }
        let cancelled = false;
        fetch(sheetUrl)
            .then((r) => (r.ok ? r.text() : ''))
            .then((text) => { if (!cancelled) setStylesheetCss(String(text || '')); })
            .catch(() => { if (!cancelled) setStylesheetCss(''); });
        return () => { cancelled = true; };
    }, [branding]);

    useEffect(() => {
        const f = (branding && branding.fonts) || {};
        ['primary', 'secondary'].forEach((key) => {
            const id = `dq-google-font-${key}`;
            const url = f[key];
            let el = document.getElementById(id);
            if (!isGoogleFontsCssUrl(url)) {
                if (el) el.remove();
                return;
            }
            if (!el) {
                el = document.createElement('link');
                el.id = id;
                el.rel = 'stylesheet';
                document.head.appendChild(el);
            }
            el.href = url.trim();
        });
    }, [branding]);

    useEffect(() => {
        const STYLE_ID = 'dq-brand-fonts';
        let el = document.getElementById(STYLE_ID);
        const f = (branding && branding.fonts) || {};
        const rules = [];
        if (isFontFileUrl(f.primary)) {
            const fmt = fontFormatHint(f.primary);
            rules.push(`@font-face{font-family:"BrandPrimaryFont";src:url("${f.primary}") format("${fmt}");font-display:swap;}`);
        }
        if (isFontFileUrl(f.secondary)) {
            const fmt = fontFormatHint(f.secondary);
            rules.push(`@font-face{font-family:"BrandSecondaryFont";src:url("${f.secondary}") format("${fmt}");font-display:swap;}`);
        }
        if (!rules.length) {
            if (el) el.remove();
            return;
        }
        if (!el) {
            el = document.createElement('style');
            el.id = STYLE_ID;
            document.head.appendChild(el);
        }
        el.textContent = rules.join('\n');
    }, [branding]);

    useEffect(() => {
        const STYLE_ID = 'dq-custom-branding';
        let el = document.getElementById(STYLE_ID);
        if (!stylesheetCss) {
            if (el) el.remove();
            return;
        }
        if (!el) {
            el = document.createElement('style');
            el.id = STYLE_ID;
            document.head.appendChild(el);
        }
        const vars = `
:root{
--brand-primary:var(--brand-primary);
--brand-secondary:var(--brand-secondary);
--brand-text:var(--brand-text);
--brand-bg:var(--brand-bg);
--brand-global-1:var(--brand-global-1);
--brand-global-2:var(--brand-global-2);
--brand-global-3:var(--brand-global-3);
}
${(isFontFileUrl(branding?.fonts?.primary) || isGoogleFontsCssUrl(branding?.fonts?.primary)) ? 'body,#root{font-family:var(--brand-font-primary),sans-serif;}' : ''}
`;
        el.textContent = vars + stylesheetCss;
    }, [stylesheetCss, branding]);

    const value = useMemo(
        () => ({
            branding,
            brandingAccess,
            userCanViewAppearance: !!(brandingAccess && brandingAccess.user_can_view),
            userCanEditBranding: !!(brandingAccess && brandingAccess.user_can_edit),
            refreshBranding,
            setBranding,
        }),
        [branding, brandingAccess, refreshBranding, setBranding],
    );

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const BrandingThemeProvider = ({ children }) => {
    const { branding } = useBranding();
    const { mode } = useColorMode();
    const themeV4 = useMemo(() => buildTheme(branding, mode), [branding, mode]);
    const themeV5 = useMemo(() => buildThemeV5(branding, mode), [branding, mode]);
    return (
        <ThemeProvider theme={themeV4}>
            <ThemeProviderV5 theme={themeV5}>
                {children}
            </ThemeProviderV5>
        </ThemeProvider>
    );
};

export default BrandingProvider;
